import { Fragment, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ConflictError } from '../api/client';
import type { PayCodesResponse, Period, PointsKeyRow } from '../api/types';
import { useApp } from '../state/AppContext';
import {
  ConflictBanner, EmptyRow, ErrorBanner, LockedBanner, Points, SortableHeader, formatLongDate, today, trim,
} from '../components/ui';

type SortColumn = 'category' | 'label' | 'points' | 'startDate';

/**
 * Scoring rules and period configuration. HR/Admin only.
 *
 * The Points Key here is temporal, which is the single biggest departure from
 * the spreadsheet: changing a value closes the current version and opens a new
 * dated one, so a score computed for a closed period never shifts under it.
 */
export function Settings() {
  const { reference, bands, can } = useApp();
  const queryClient = useQueryClient();
  const canEdit = can('settings', 'edit');

  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  const [sort, setSort] = useState<{ column: SortColumn; dir: 'asc' | 'desc' }>({ column: 'category', dir: 'asc' });
  const [historyOpen, setHistoryOpen] = useState<string | null>(null);
  const [revising, setRevising] = useState<{ id: number; points: string; startDate: string } | null>(null);

  const current = reference.periods.find((p) => p.isCurrent) ?? reference.periods[0];
  const [periodDraft, setPeriodDraft] = useState<Period>(current);

  const pointsKey = useQuery({ queryKey: ['points-key'], queryFn: () => api.get<PointsKeyRow[]>('/api/points-key') });

  const payCodes = useQuery({
    queryKey: ['pay-codes'],
    queryFn: () => api.get<PayCodesResponse>('/api/pay-codes'),
  });

  const savePayCode = useMutation({
    mutationFn: (v: { code: string; isExcused: boolean; isNoPunchExpected: boolean }) =>
      api.put('/api/pay-codes', v),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pay-codes'] }),
  });

  const saveFacility = useMutation({
    mutationFn: (v: { facilityId: number; code: string; name: string; isActive: boolean }) =>
      v.facilityId > 0
        ? api.put(`/api/facilities/${v.facilityId}`, v)
        : api.post('/api/facilities', v),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<Record<string, string | null>>('/api/settings'),
  });

  const saveSettings = useMutation({
    mutationFn: (v: Record<string, string | null>) => api.put('/api/settings', v),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  const rows = useMemo(() => {
    const list = [...(pointsKey.data ?? [])];
    const dir = sort.dir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      switch (sort.column) {
        case 'label': return a.label.localeCompare(b.label) * dir;
        case 'points': return (a.points - b.points) * dir;
        case 'startDate': return a.startDate.localeCompare(b.startDate) * dir;
        default: return (a.category.localeCompare(b.category) || a.label.localeCompare(b.label)) * dir;
      }
    });
    return list;
  }, [pointsKey.data, sort]);

  const isLocked = periodDraft.status === 'locked';

  const savePeriod = useMutation({
    mutationFn: () =>
      api.put(`/api/periods/${periodDraft.periodId}`, {
        startDate: periodDraft.startDate.slice(0, 10),
        endDate: periodDraft.endDate.slice(0, 10),
        gracePeriodDays: periodDraft.gracePeriodDays,
        status: periodDraft.status,
        rowVersionNo: periodDraft.rowVersionNo,
      }),
    onSuccess: () => queryClient.invalidateQueries(),
    onError: (e) => (e instanceof ConflictError ? setConflict(e.message) : setError(e.message)),
  });

  const revise = useMutation({
    mutationFn: (v: { id: number; points: number; startDate: string }) =>
      api.put(`/api/points-key/${v.id}`, { points: v.points, startDate: v.startDate }),
    onSuccess: () => { setRevising(null); queryClient.invalidateQueries({ queryKey: ['points-key'] }); },
    onError: (e) => setError(e.message),
  });

  const add = useMutation({
    mutationFn: (v: { category: string; label: string; points: number; startDate: string }) =>
      api.post('/api/points-key', { ...v, note: null }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['points-key'] }),
    onError: (e) => setError(e.message),
  });

  const toggleSort = (column: string) =>
    setSort((s) => ({ column: column as SortColumn, dir: s.column === column && s.dir === 'asc' ? 'desc' : 'asc' }));

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Settings</div>
          <div className="page-subtitle">Scoring rules and period configuration</div>
        </div>
      </div>

      {conflict && <ConflictBanner message={conflict} onReload={() => { setConflict(null); queryClient.invalidateQueries(); }} />}
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 16, marginBottom: 24 }}>
        <div className="card">
          <div className="section-label">CURRENT PERIOD — {periodDraft.name}</div>
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            <div>
              <div className="field-label">START</div>
              <input type="date" className="control" disabled={!canEdit} value={periodDraft.startDate.slice(0, 10)}
                onChange={(e) => setPeriodDraft({ ...periodDraft, startDate: e.target.value })} />
            </div>
            <div>
              <div className="field-label">END</div>
              <input type="date" className="control" disabled={!canEdit} value={periodDraft.endDate.slice(0, 10)}
                onChange={(e) => setPeriodDraft({ ...periodDraft, endDate: e.target.value })} />
            </div>
            <div>
              {/* Per period, not a system default — so a quarter that runs long
                  can get a wider window without changing the standard. */}
              <div className="field-label">GRACE PERIOD LENGTH</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="number" className="control" style={{ width: 62 }} disabled={!canEdit}
                  value={periodDraft.gracePeriodDays}
                  onChange={(e) => setPeriodDraft({ ...periodDraft, gracePeriodDays: Number(e.target.value) })} />
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>days</span>
              </div>
            </div>
            {canEdit && (
              <div style={{ alignSelf: 'flex-end' }}>
                <button className="btn btn--primary" disabled={savePeriod.isPending} onClick={() => savePeriod.mutate()}>
                  {savePeriod.isPending ? 'Saving…' : 'Save period'}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="section-label" style={{ marginBottom: 10 }}>PERIOD STATUS</div>
          <select className="control" style={{ width: '100%', marginBottom: 8 }} disabled={!canEdit}
            value={periodDraft.status}
            onChange={(e) => setPeriodDraft({ ...periodDraft, status: e.target.value as Period['status'] })}>
            <option value="open">Open</option>
            <option value="grace">Grace period</option>
            <option value="locked">Locked</option>
          </select>
          <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>
            Next auto-transition:{' '}
            <strong style={{ color: 'var(--text-secondary)' }}>{formatLongDate(periodDraft.endDate)}</strong>
            {' '}then {periodDraft.gracePeriodDays} grace days
          </div>
        </div>
      </div>

      {isLocked && <LockedBanner periodName={periodDraft.name} />}

      <div className="section-label">POINTS KEY</div>

      {canEdit && <AddEntry disabled={isLocked} categories={[...new Set(rows.map((r) => r.category))]} onAdd={add.mutate} />}

      <div className="table-wrap" style={{ marginBottom: 24, opacity: isLocked ? 0.55 : 1 }}>
        <table className="table">
          <thead>
            <tr>
              <SortableHeader label="CATEGORY" column="category" sort={sort} onSort={toggleSort} />
              <SortableHeader label="INFRACTION / REWARD" column="label" sort={sort} onSort={toggleSort} />
              <SortableHeader label="POINTS" column="points" sort={sort} onSort={toggleSort} className="num" />
              <th>NOTE</th>
              <SortableHeader label="STARTS" column="startDate" sort={sort} onSort={toggleSort} />
              <th>STATUS</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {pointsKey.isLoading && <EmptyRow colSpan={7}>Loading…</EmptyRow>}
            {rows.map((p) => (
              <Fragment key={p.entryKey}>
                <tr>
                  <td>{p.category}</td>
                  <td style={{ color: 'var(--text)' }}>{p.label}</td>
                  <td className="num">
                    {revising?.id === p.pointsKeyEntryId ? (
                      <input type="number" className="control" style={{ width: 70, textAlign: 'right' }}
                        value={revising.points} autoFocus
                        onChange={(e) => setRevising({ ...revising, points: e.target.value })} />
                    ) : (
                      <Points value={p.points} />
                    )}
                  </td>
                  <td style={{ fontSize: 11.5, color: 'var(--note)', maxWidth: 260, lineHeight: 1.4 }}>{p.note}</td>
                  <td style={{ whiteSpace: 'nowrap', color: 'var(--muted)', fontSize: 11.5 }}>
                    {revising?.id === p.pointsKeyEntryId ? (
                      <input type="date" className="control" value={revising.startDate}
                        onChange={(e) => setRevising({ ...revising, startDate: e.target.value })} />
                    ) : (
                      formatLongDate(p.startDate)
                    )}
                  </td>
                  <td>
                    <span className={`pill pill--${p.status === 'Active' ? 'good' : p.status === 'Scheduled' ? 'attention' : 'urgent'}`}>
                      {p.status}
                    </span>
                  </td>
                  <td className="num">
                    {revising?.id === p.pointsKeyEntryId ? (
                      <span style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button className="btn btn--sm" style={{ border: 'none', background: 'transparent', color: 'var(--blue)' }}
                          onClick={() => revise.mutate({
                            id: revising.id, points: Number(revising.points), startDate: revising.startDate,
                          })}>
                          save
                        </button>
                        <button className="btn btn--sm" style={{ border: 'none', background: 'transparent', color: 'var(--muted)' }}
                          onClick={() => setRevising(null)}>
                          cancel
                        </button>
                      </span>
                    ) : (
                      <span style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        {canEdit && !isLocked && (
                          <button className="btn btn--sm" style={{ border: 'none', background: 'transparent', color: 'var(--blue)' }}
                            onClick={() => setRevising({ id: p.pointsKeyEntryId, points: String(p.points), startDate: today() })}>
                            edit
                          </button>
                        )}
                        {p.history.length > 0 && (
                          <button className="btn btn--sm"
                            style={{ border: 'none', background: 'transparent', color: 'var(--muted)', textDecoration: 'underline' }}
                            onClick={() => setHistoryOpen(historyOpen === p.entryKey ? null : p.entryKey)}>
                            history
                          </button>
                        )}
                      </span>
                    )}
                  </td>
                </tr>

                {historyOpen === p.entryKey && (
                  <tr className="history-row">
                    <td colSpan={7}>
                      <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--muted)', letterSpacing: 0.3, marginBottom: 5 }}>
                        PRIOR VALUES
                      </div>
                      {p.history.map((h, i) => (
                        <div key={i} style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
                          {formatLongDate(h.startDate)} – {h.endDate ? formatLongDate(h.endDate) : 'open'}: {trim(h.points)} pts
                        </div>
                      ))}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {canEdit && !isLocked && (
        <div className="meta" style={{ marginBottom: 24 }}>
          Editing a value does not overwrite it — the current version closes the day before the new start date and a new
          dated version opens, so incidents already logged keep the value that applied on their own date.
        </div>
      )}

      <div className="section-label">SCORE → ACTION BANDS</div>
      <div className="table-wrap card--flush">
        {bands.map((b) => (
          <div key={b.label} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '11px 18px', borderBottom: '1px solid var(--rule)',
          }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{b.label}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span className="meta">{b.rangeLabel}</span>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: b.color }} />
            </div>
          </div>
        ))}
      </div>

      <ScoringRules
        settings={settings.data}
        readOnly={!can('settings', 'edit')}
        onSave={(v) => saveSettings.mutate(v)}
      />

      <PayCodes
        data={payCodes.data}
        loading={payCodes.isLoading}
        readOnly={!can('settings', 'edit')}
        onSave={(v) => savePayCode.mutate(v)}
      />

      <ScorecardScope
        settings={settings.data}
        knownDepartments={reference.departments}
        readOnly={!can('settings', 'edit')}
        onSave={(v) => saveSettings.mutate(v)}
      />

      <Facilities
        rows={reference.facilities}
        readOnly={!can('settings', 'edit')}
        onSave={(v) => saveFacility.mutate(v)}
      />

    </div>
  );
}

function AddEntry({
  disabled, categories, onAdd,
}: {
  disabled: boolean;
  categories: string[];
  onAdd: (v: { category: string; label: string; points: number; startDate: string }) => void;
}) {
  const [category, setCategory] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [label, setLabel] = useState('');
  const [points, setPoints] = useState('-1');
  const [startDate, setStartDate] = useState(today());

  const isNew = category === '__new';
  const effectiveCategory = isNew ? newCategory : category;
  const ready = !disabled && effectiveCategory.trim() !== '' && label.trim() !== '';

  return (
    <div style={{
      background: 'var(--surface-muted)', border: '1px solid var(--divider)', borderRadius: 'var(--radius-card)',
      padding: '14px 16px', marginBottom: 14, opacity: disabled ? 0.55 : 1,
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', letterSpacing: 0.3, marginBottom: 10 }}>
        ADD CATEGORY OR ENTRY
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.4fr 90px 130px 90px', gap: 10, alignItems: 'end' }}>
        <div>
          <label className="field-label">CATEGORY</label>
          <select className="control" style={{ width: '100%' }} disabled={disabled}
            value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">Select…</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            <option value="__new">+ New category…</option>
          </select>
        </div>
        <div>
          <label className="field-label">NEW CATEGORY NAME</label>
          <input className="control" style={{ width: '100%' }} disabled={disabled || !isNew}
            placeholder={isNew ? 'e.g. Vehicle' : '—'} value={newCategory} onChange={(e) => setNewCategory(e.target.value)} />
        </div>
        <div>
          <label className="field-label">INFRACTION / REWARD</label>
          <input className="control" style={{ width: '100%' }} disabled={disabled}
            placeholder="e.g. Vehicle inspection missed" value={label} onChange={(e) => setLabel(e.target.value)} />
        </div>
        <div>
          <label className="field-label">POINTS</label>
          <input type="number" className="control" style={{ width: '100%' }} disabled={disabled}
            value={points} onChange={(e) => setPoints(e.target.value)} />
        </div>
        <div>
          <label className="field-label">STARTS</label>
          <input type="date" className="control" style={{ width: '100%' }} disabled={disabled}
            value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </div>
        <button className="btn btn--primary" disabled={!ready}
          onClick={() => {
            onAdd({ category: effectiveCategory.trim(), label: label.trim(), points: Number(points), startDate });
            setLabel(''); setNewCategory('');
          }}>
          Add
        </button>
      </div>
      <div style={{ fontSize: 10.5, color: 'var(--muted)', marginTop: 6 }}>
        A future start date pre-stages an entry — it shows as Scheduled until that date, and 0 points is a valid value
        for anything that triggers a review rather than a deduction.
      </div>
    </div>
  );
}

/**
 * Pay codes decide whether a day is scored at all, so these two checkboxes are
 * the highest-leverage reference data in the system. Until this existed they
 * were reachable only by direct SQL.
 *
 * Unclassified codes are listed first and deliberately loudly: the generator
 * holds those days at zero points rather than guessing, so an unclassified
 * code is unfinished work, not a steady state.
 */
function PayCodes({
  data, loading, readOnly, onSave,
}: {
  data?: PayCodesResponse;
  loading: boolean;
  readOnly: boolean;
  onSave: (v: { code: string; isExcused: boolean; isNoPunchExpected: boolean }) => void;
}) {
  const unclassified = data?.unclassified ?? [];
  return (
    <>
      <div className="section-label" style={{ marginTop: 24 }}>PAY CODES</div>
      <div className="meta" style={{ marginBottom: 10 }}>
        <strong>Excused</strong> means the day is not evaluated at all — no lateness check, no absence, auto-approved.{' '}
        <strong>No punch expected</strong> means a missing punch is normal, so no absence is charged, but the day still
        waits for review. Neither box ticked means the day is measured against the schedule, where an absence costs points.
      </div>

      {unclassified.length > 0 && (
        <div className="banner banner--conflict" style={{ marginBottom: 12 }}>
          <strong>{unclassified.length} pay {unclassified.length === 1 ? 'code' : 'codes'} in your punch data {unclassified.length === 1 ? 'is' : 'are'} not classified.</strong>{' '}
          Those days are held at 0 points and are waiting for review. Tick the boxes below, then use
          <strong> Rebuild daily records</strong> on the Daily Summary screen.
        </div>
      )}

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>CODE</th>
              <th className="num">EXCUSED</th>
              <th className="num">NO PUNCH EXPECTED</th>
              <th>EFFECT</th>
            </tr>
          </thead>
          <tbody>
            {loading && <EmptyRow colSpan={4}>Loading…</EmptyRow>}

            {unclassified.map((u) => (
              <PayCodeRowView
                key={`u-${u.code}`}
                code={u.code}
                isExcused={false}
                isNoPunchExpected={false}
                unclassifiedRows={u.punchRows}
                readOnly={readOnly}
                onSave={onSave}
              />
            ))}

            {(data?.classified ?? []).map((c) => (
              <PayCodeRowView
                key={c.code}
                code={c.code}
                isExcused={c.isExcused}
                isNoPunchExpected={c.isNoPunchExpected}
                readOnly={readOnly}
                onSave={onSave}
              />
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function PayCodeRowView({
  code, isExcused, isNoPunchExpected, unclassifiedRows, readOnly, onSave,
}: {
  code: string;
  isExcused: boolean;
  isNoPunchExpected: boolean;
  unclassifiedRows?: number;
  readOnly: boolean;
  onSave: (v: { code: string; isExcused: boolean; isNoPunchExpected: boolean }) => void;
}) {
  const effect = unclassifiedRows !== undefined
    ? `Unclassified — ${unclassifiedRows} row${unclassifiedRows === 1 ? '' : 's'} held at 0 points, pending`
    : isExcused
      ? 'Not evaluated — 0 points, auto-approved'
      : isNoPunchExpected
        ? 'Missing punch is normal — 0 points, pending'
        : 'Measured against the schedule';

  return (
    <tr style={unclassifiedRows !== undefined ? { background: 'var(--orange-tint)' } : undefined}>
      <td className="strong">{code}</td>
      <td className="num">
        <input
          type="checkbox"
          checked={isExcused}
          disabled={readOnly}
          onChange={(e) => onSave({ code, isExcused: e.target.checked, isNoPunchExpected })}
        />
      </td>
      <td className="num">
        <input
          type="checkbox"
          checked={isNoPunchExpected}
          disabled={readOnly}
          onChange={(e) => onSave({ code, isExcused, isNoPunchExpected: e.target.checked })}
        />
      </td>
      <td className="meta">{effect}</td>
    </tr>
  );
}

/**
 * Which departments the scorecard covers. Blank means all of them, so an
 * unconfigured system behaves as it always did.
 *
 * Departments are typed rather than picked from a fixed list, because they
 * belong to the upstream HR system — this app is not their source of truth and
 * should not keep a master copy that drifts. That also means the scope can be
 * set before any data is imported, which a derived list cannot do: with an
 * empty database there are no departments to derive.
 *
 * Once employees exist, their distinct departments are offered as one-click
 * suggestions — convenience, not a constraint.
 */
function ScorecardScope({
  settings, knownDepartments, readOnly, onSave,
}: {
  settings?: Record<string, string | null>;
  knownDepartments: string[];
  readOnly: boolean;
  onSave: (v: Record<string, string | null>) => void;
}) {
  const [draft, setDraft] = useState('');

  const selected = (settings?.ScorecardDepartments ?? '')
    .split(',').map((x) => x.trim()).filter(Boolean);

  const commit = (next: string[]) =>
    onSave({ ScorecardDepartments: [...new Set(next)].join(',') });

  const add = (value: string) => {
    const v = value.trim();
    if (!v || selected.some((x) => x.toLowerCase() === v.toLowerCase())) return;
    commit([...selected, v]);
    setDraft('');
  };

  const suggestions = knownDepartments.filter(
    (d) => !selected.some((x) => x.toLowerCase() === d.toLowerCase()),
  );

  return (
    <>
      <div className="section-label" style={{ marginTop: 24 }}>SCORECARD SCOPE</div>
      <div className="meta" style={{ marginBottom: 10 }}>
        Which departments the scorecard covers. Leave it empty to score <strong>everyone</strong>.
        Employees outside these departments still import and keep their full HR record — they simply
        generate no daily records and stay off the scorecard. Change this, then use
        <strong> Rebuild daily records</strong> on the Daily Summary screen to apply it to history.
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        {selected.length === 0 && (
          <span className="meta" style={{ fontStyle: 'italic' }}>No departments set — every employee is scored.</span>
        )}
        {selected.map((d) => (
          <span
            key={d}
            className="pill"
            style={{
              background: 'var(--blue-tint)', color: 'var(--blue)', fontWeight: 700,
              display: 'inline-flex', alignItems: 'center', gap: 8, padding: '5px 10px',
            }}
          >
            {d}
            {!readOnly && (
              <button
                onClick={() => commit(selected.filter((x) => x !== d))}
                title={`Remove ${d}`}
                style={{
                  border: 'none', background: 'none', cursor: 'pointer', color: 'inherit',
                  fontSize: 15, lineHeight: 1, padding: 0,
                }}
              >
                ×
              </button>
            )}
          </span>
        ))}
      </div>

      {!readOnly && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <input
            className="control"
            style={{ maxWidth: 320 }}
            placeholder="Department name, e.g. BW2 - Plant"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') add(draft); }}
          />
          <button className="btn btn--sm" disabled={!draft.trim()} onClick={() => add(draft)}>Add</button>
        </div>
      )}

      {suggestions.length > 0 && (
        <div className="meta" style={{ marginTop: 4 }}>
          From imported data:{' '}
          {suggestions.map((d) => (
            <button
              key={d}
              className="btn btn--sm"
              disabled={readOnly}
              onClick={() => add(d)}
              style={{ marginRight: 6, marginBottom: 6 }}
            >
              + {d}
            </button>
          ))}
        </div>
      )}

      {knownDepartments.length === 0 && (
        <div className="meta" style={{ marginTop: 4, fontStyle: 'italic' }}>
          No employees imported yet, so there is nothing to suggest — type the department names you want.
        </div>
      )}
    </>
  );
}

/**
 * The numbers that drive every score. They have always been live in
 * dbo.Settings and read on every generation — there was simply no way to see
 * or change them without calling the API by hand.
 *
 * The reward figure is deliberately absent: rewards are not capped by the
 * system, so it is guidance shown where rewards are granted, not a setting.
 */
function ScoringRules({
  settings, readOnly, onSave,
}: {
  settings?: Record<string, string | null>;
  readOnly: boolean;
  onSave: (v: Record<string, string | null>) => void;
}) {
  const [draft, setDraft] = useState<Record<string, string> | null>(null);

  const current = {
    LateThresholdMinutes: settings?.LateThresholdMinutes ?? '',
    StartingScore: settings?.StartingScore ?? '',
  };
  const value = draft ?? current;
  const dirty = draft !== null && Object.keys(current).some((k) => draft[k] !== (current as Record<string, string>)[k]);

  const field = (key: keyof typeof current, label: string, hint: string) => (
    <label>
      <div className="field-label">{label}</div>
      <input
        className="control"
        type="number"
        style={{ width: 90 }}
        disabled={readOnly}
        value={value[key]}
        onChange={(e) => setDraft({ ...value, [key]: e.target.value })}
      />
      <div className="meta" style={{ marginTop: 4 }}>{hint}</div>
    </label>
  );

  return (
    <>
      <div className="section-label" style={{ marginTop: 24 }}>SCORING RULES</div>
      <div className="meta" style={{ marginBottom: 10 }}>
        Applied when daily records are generated. Changing a value here and regenerating recomputes history,
        because scores are derived from the event ledger rather than stored.
      </div>
      <div style={{ display: 'flex', gap: 28, alignItems: 'flex-start', marginBottom: 12 }}>
        {field('LateThresholdMinutes', 'LATE THRESHOLD (MIN)', 'Minutes past the scheduled start before a day is flagged Late.')}
        {field('StartingScore', 'STARTING SCORE', 'Every employee starts each period here.')}
      </div>
      {!readOnly && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 4 }}>
          <button className="btn btn--primary btn--sm" disabled={!dirty} onClick={() => { onSave(value); setDraft(null); }}>
            Save scoring rules
          </button>
          {dirty && <button className="btn btn--sm" onClick={() => setDraft(null)}>Cancel</button>}
        </div>
      )}
    </>
  );
}

/**
 * Facilities are hand-maintained: a short list, not a bulk feed. Employee
 * imports validate Location against these codes, so without a way to add one
 * here a new facility from the HR export would reject every employee at it
 * with no way to fix it short of SQL.
 *
 * The code itself is not editable. Employees and user roles reference it by
 * value across a database boundary where no foreign key can enforce it, so a
 * rename would silently orphan both.
 */
function Facilities({
  rows, readOnly, onSave,
}: {
  rows: { facilityId: number; code: string; name: string; isActive: boolean }[];
  readOnly: boolean;
  onSave: (v: { facilityId: number; code: string; name: string; isActive: boolean }) => void;
}) {
  const [draft, setDraft] = useState<null | {
    facilityId: number; code: string; name: string; isActive: boolean;
  }>(null);

  const blank = () => ({ facilityId: 0, code: '', name: '', isActive: true });

  const commit = () => {
    if (!draft) return;
    onSave({
      facilityId: draft.facilityId,
      code: draft.code.trim(),
      name: draft.name.trim(),
      isActive: draft.isActive,
    });
    setDraft(null);
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 24 }}>
        <div className="section-label" style={{ marginTop: 0 }}>FACILITIES</div>
        {!readOnly && !draft && (
          <button className="btn btn--sm" onClick={() => setDraft(blank())}>Add facility</button>
        )}
      </div>
      <div className="meta" style={{ marginBottom: 10 }}>
        Hand-maintained here rather than imported — a short list, not a bulk feed. An employee's location must
        match one of these codes or the Employees import rejects the row, so a facility appearing in the HR export
        needs adding here first. The code cannot be changed afterwards: employees and location-scoped users
        reference it by value, and renaming would orphan them.
      </div>

      {draft && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 12 }}>
            <label>
              <div className="field-label">CODE</div>
              <input
                className="control"
                value={draft.code}
                disabled={draft.facilityId > 0}
                placeholder="Indy-Midwest"
                onChange={(e) => setDraft({ ...draft, code: e.target.value })}
              />
              {draft.facilityId > 0 && <div className="meta" style={{ marginTop: 4 }}>Fixed once created.</div>}
            </label>
            <label style={{ gridColumn: 'span 2' }}>
              <div className="field-label">NAME</div>
              <input className="control" value={draft.name} placeholder="Indianapolis Midwest"
                onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </label>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 12 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <input type="checkbox" checked={draft.isActive}
                onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })} />
              Active
            </label>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button className="btn" onClick={() => setDraft(null)}>Cancel</button>
            <button className="btn btn--primary" disabled={!draft.code.trim() || !draft.name.trim()} onClick={commit}>
              Save
            </button>
          </div>
        </div>
      )}

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr><th>CODE</th><th>NAME</th>{!readOnly && <th />}</tr>
          </thead>
          <tbody>
            {rows.map((f) => (
              <tr key={f.facilityId} style={f.isActive ? undefined : { opacity: 0.55 }}>
                <td className="strong">{f.code}</td>
                <td>{f.name}{f.isActive ? '' : ' · inactive'}</td>
                {!readOnly && (
                  <td className="num">
                    <button
                      className="btn btn--sm"
                      onClick={() => setDraft({
                        facilityId: f.facilityId, code: f.code, name: f.name, isActive: f.isActive,
                      })}
                    >
                      Edit
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
